import { Button, Paper } from "@material-ui/core";
import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";

import _ from "lodash";
import "./Crud.css";
import Loader from "./loader/Loader";

function ReportAgentPosition({
  controlledData,
  className = "",
  initialData = [],
  getParams,
  getProcess,
}) {
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("userInfo")),
    []
  );
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const cancelTokenSource = useRef(null);

  useEffect(() => {
    if (controlledData) {
      setData(controlledData);
    }
  }, [controlledData]);

  /**
   * GET
   */
  const requestData = useCallback(() => {
    const currentCancelToken = cancelTokenSource.current;
    cancelTokenSource.current = axios.CancelToken.source();

    if (currentCancelToken) {
      currentCancelToken.cancel("Canceling previous request");
    }

    setLoading(true);

    const url =
      process.env.REACT_APP_API2_URL + "/Reports/Report_Agent_Position";
    axios
      .post(
        url,
        getParams instanceof Function ? getParams(userInfo) : undefined,
        {
          cancelToken: cancelTokenSource.current.token,
        }
      )
      .then((response) => response.data)
      .then((data) => {
        const params = getParams(userInfo);
        if (params.Sport) {
          data = data.filter((item) => item.IdSport === params.Sport);
        }

        const groupedData = _.groupBy(data, "IdGame");
        _.forEach(groupedData, (values, key) => {
          groupedData[key] = values.map((item) => {
            const value = {
              ...item,
              Id: item.HomeNumber + item.IdGame,
            };
            return getProcess instanceof Function ? getProcess(value) : value;
          });
        });

        setData(groupedData);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error(err);
        }
      });

    return () => {
      currentCancelToken.cancel("Component unmounted");
    };
  }, [userInfo, getProcess, getParams]);

  const onSearch = useCallback(() => {
    requestData();
  }, [requestData]);

  /**
   * TABLE OPTIONS
   */
  const options = {
    page: 1, // which page you want to show as default
    sizePerPage: 20, // which size per page you want to locate as default
    pageStartIndex: 1, // where to start counting the pages
    paginationSize: 5, // the pagination bar size.
    prePage: "Prev", // Previous page button text
    nextPage: "Next", // Next page button text
    firstPage: "First", // First page button text
    lastPage: "Last", // Last page button text
    paginationShowsTotal: true, // Accept bool or function
    paginationPosition: "top", // default is bottom, top and both is all available
    withFirstAndLast: true, // Hide the going to First and Last page button
  };

  const nflNba = ({
    Id,
    VisitorNumber,
    VisitorName,
    VisitorPitcher,
    VLine,
    VLineMM,
    VTotal,
    VTotalMM,
    VSpread,
    VSpreadMM,
    HomeNumber,
    HomeName,
    HomePitcher,
    HLine,
    HLineMM,
    HTotal,
    HTotalMM,
    HSpread,
    HSpreadMM,
  }) => (
    <BootstrapTable
      className="text-xxs compact-table"
      data={[
        {
          Id: `${Id}V`,
          Game: `${VisitorNumber} ${VisitorName} (${VisitorPitcher})`,
          MoneyLine: VLine ? VLine : "",
          MoneyLineMinMax: VLineMM,
          Total: VTotal ? VTotal : "",
          TotalMinMax: VTotalMM,
          Spread: VSpread ? VSpread : "",
          SpreadMinMax: VSpreadMM,
        },
        {
          Id: `${Id}H`,
          Game: `${HomeNumber} ${HomeName} (${HomePitcher})`,
          MoneyLine: HLine ? HLine : "",
          MoneyLineMinMax: HLineMM,
          Total: HTotal ? HTotal : "",
          TotalMinMax: HTotalMM,
          Spread: HSpread ? HSpread : "",
          SpreadMinMax: HSpreadMM,
        },
      ]}
      options={options}
      version="4"
    >
      <TableHeaderColumn dataSort dataField="Id" isKey hidden>
        Id
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Game">
        Game
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Spread" dataAlign="right">
        Spread
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="SpreadMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Total" dataAlign="right">
        Total
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="TotalMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLine" dataAlign="right">
        $-Line Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLineMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
    </BootstrapTable>
  );

  const mlb = ({
    Id,
    VisitorNumber,
    VisitorName,
    VisitorPitcher,
    VLine,
    VLineMM,
    VTotal,
    VTotalMM,
    VSpread,
    VSpreadMM,
    HomeNumber,
    HomeName,
    HomePitcher,
    HLine,
    HLineMM,
    HTotal,
    HTotalMM,
    HSpread,
    HSpreadMM,
  }) => (
    <BootstrapTable
      className="text-xxs compact-table"
      data={[
        {
          Id: `${Id}V`,
          Game: `${VisitorNumber} ${VisitorName}`,
          MoneyLine: VLine ? VLine : "",
          MoneyLineMinMax: VLineMM,
          Total: VTotal ? VTotal : "",
          TotalMinMax: VTotalMM,
          Spread: VSpread ? VSpread : "",
          SpreadMinMax: VSpreadMM,
        },
        {
          Id: `${Id}H`,
          Game: `${HomeNumber} ${HomeName}`,
          MoneyLine: HLine ? HLine : "",
          MoneyLineMinMax: HLineMM,
          Total: HTotal ? HTotal : "",
          TotalMinMax: HTotalMM,
          Spread: HSpread ? HSpread : "",
          SpreadMinMax: HSpreadMM,
        },
      ]}
      options={options}
      version="4"
    >
      <TableHeaderColumn dataSort dataField="Id" isKey hidden>
        Id
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Game">
        Game
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Total" dataAlign="right">
        Total
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="TotalMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLine" dataAlign="right">
        $-Line Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLineMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Spread" dataAlign="right">
        RunLine
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="SpreadMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
    </BootstrapTable>
  );

  const nhl = ({
    Id,
    VisitorNumber,
    VisitorName,
    VLine,
    VLineMM,
    VTotal,
    VTotalMM,
    VSpread,
    VSpreadMM,
    VAmeric,
    VAmericMM,
    HomeNumber,
    HomeName,
    HLine,
    HLineMM,
    HTotal,
    HTotalMM,
    HSpread,
    HSpreadMM,
    HAmeric,
    HAmericMM,
  }) => (
    <BootstrapTable
      className="text-xxs compact-table"
      data={[
        {
          Id: `${Id}V`,
          Game: `${VisitorNumber} ${VisitorName}`,
          MoneyLine: VLine ? VLine : "",
          MoneyLineMinMax: VLineMM,
          Total: VTotal ? VTotal : "",
          TotalMinMax: VTotalMM,
          Spread: VSpread ? VSpread : "",
          SpreadMinMax: VSpreadMM,
          AmericanSpread: VAmeric ? VAmeric : "",
          AmericanSpreadMinMax: VAmericMM,
        },
        {
          Id: `${Id}H`,
          Game: `${HomeNumber} ${HomeName}`,
          MoneyLine: HLine ? HLine : "",
          MoneyLineMinMax: HLineMM,
          Total: HTotal ? HTotal : "",
          TotalMinMax: HTotalMM,
          Spread: HSpread ? HSpread : "",
          SpreadMinMax: HSpreadMM,
          AmericanSpread: HAmeric ? HAmeric : "",
          AmericanSpreadMinMax: HAmericMM,
        },
      ]}
      options={options}
      version="4"
    >
      <TableHeaderColumn dataSort dataField="Id" isKey hidden>
        Id
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Game">
        Game
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Spread" dataAlign="right">
        C-Line
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="SpreadMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLine" dataAlign="right">
        $-Line Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLineMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Total" dataAlign="right">
        Total
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="TotalMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="AmericanSpread" dataAlign="right">
        A-Line
      </TableHeaderColumn>
      <TableHeaderColumn
        dataSort
        dataField="AmericanSpreadMinMax"
        dataAlign="left"
      >
        Min/Max
      </TableHeaderColumn>
    </BootstrapTable>
  );

  const cbb = ({
    Id,
    VisitorNumber,
    VisitorName,
    VLine,
    VLineMM,
    VTotal,
    VTotalMM,
    VSpread,
    VSpreadMM,
    HomeNumber,
    HomeName,
    HLine,
    HLineMM,
    HTotal,
    HTotalMM,
    HSpread,
    HSpreadMM,
  }) => (
    <BootstrapTable
      className="text-xxs compact-table"
      data={[
        {
          Id: `${Id}V`,
          Game: `${VisitorNumber} ${VisitorName}`,
          MoneyLine: VLine ? VLine : "",
          MoneyLineMinMax: VLineMM,
          Total: VTotal ? VTotal : "",
          TotalMinMax: VTotalMM,
          Spread: VSpread ? VSpread : "",
          SpreadMinMax: VSpreadMM,
        },
        {
          Id: `${Id}H`,
          Game: `${HomeNumber} ${HomeName}`,
          MoneyLine: HLine ? HLine : "",
          MoneyLineMinMax: HLineMM,
          Total: HTotal ? HTotal : "",
          TotalMinMax: HTotalMM,
          Spread: HSpread ? HSpread : "",
          SpreadMinMax: HSpreadMM,
        },
      ]}
      options={options}
      version="4"
    >
      <TableHeaderColumn dataSort dataField="Id" isKey hidden>
        Id
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Game">
        Game
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Spread" dataAlign="right">
        Spread
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="SpreadMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Total" dataAlign="right">
        Total
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="TotalMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLine" dataAlign="right">
        $-Line Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLineMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
    </BootstrapTable>
  );

  const soc = ({
    Id,
    VisitorNumber,
    VisitorName,
    VLine,
    VLineMM,
    VTotal,
    VTotalMM,
    VSpread,
    VSpreadMM,
    HomeNumber,
    HomeName,
    HLine,
    HLineMM,
    HTotal,
    HTotalMM,
    HSpread,
    HSpreadMM,
  }) => (
    <BootstrapTable
      className="text-xxs compact-table"
      data={[
        {
          Id: `${Id}V`,
          Game: `${VisitorNumber} ${VisitorName}`,
          MoneyLine: VLine ? VLine : "",
          MoneyLineMinMax: VLineMM,
          Total: VTotal ? VTotal : "",
          TotalMinMax: VTotalMM,
          Spread: VSpread ? VSpread : "",
          SpreadMinMax: VSpreadMM,
        },
        {
          Id: `${Id}H`,
          Game: `${HomeNumber} ${HomeName}`,
          MoneyLine: HLine ? HLine : "",
          MoneyLineMinMax: HLineMM,
          Total: HTotal ? HTotal : "",
          TotalMinMax: HTotalMM,
          Spread: HSpread ? HSpread : "",
          SpreadMinMax: HSpreadMM,
        },
        {
          Id: `${Id}D`,
          Game: `Draw`,
        },
      ]}
      options={options}
      version="4"
    >
      <TableHeaderColumn dataSort dataField="Id" isKey hidden>
        Id
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Game">
        Game
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLine" dataAlign="right">
        $-Line Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLineMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Total" dataAlign="right">
        Total
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="TotalMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Spread" dataAlign="right">
        Goals
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="SpreadMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
    </BootstrapTable>
  );

  const mu = ({
    Id,
    VisitorNumber,
    VisitorName,
    VLine,
    VLineMM,
    VTotal,
    VTotalMM,
    HomeNumber,
    HomeName,
    HLine,
    HLineMM,
    HTotal,
    HTotalMM,
  }) => (
    <BootstrapTable
      className="text-xxs compact-table"
      data={[
        {
          Id: `${Id}V`,
          Game: `${VisitorNumber} ${VisitorName}`,
          MoneyLine: VLine ? VLine : "",
          MoneyLineMinMax: VLineMM,
          Total: VTotal ? VTotal : "",
          TotalMinMax: VTotalMM,
        },
        {
          Id: `${Id}H`,
          Game: `${HomeNumber} ${HomeName}`,
          MoneyLine: HLine ? HLine : "",
          MoneyLineMinMax: HLineMM,
          Total: HTotal ? HTotal : "",
          TotalMinMax: HTotalMM,
        },
      ]}
      options={options}
      version="4"
    >
      <TableHeaderColumn dataSort dataField="Id" isKey hidden>
        Id
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Game">
        Game
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLine" dataAlign="right">
        $-Line Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="MoneyLineMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="Total" dataAlign="right">
        Total
      </TableHeaderColumn>
      <TableHeaderColumn dataSort dataField="TotalMinMax" dataAlign="left">
        Min/Max
      </TableHeaderColumn>
    </BootstrapTable>
  );

  const tnt = (values) => (
    <>
      <h3>{values?.[0]?.VisitorName}</h3>
      <BootstrapTable
        className="text-xxs"
        data={values.map(
          ({ Id, HomeNumber, HomeName, HSpread, HSpreadMM }) => ({
            Id: `${Id}H`,
            Game: `${HomeNumber} ${HomeName}`,
            Odds: HSpread ? HSpread : "",
            OddsMinMax: HSpreadMM,
          })
        )}
        options={options}
        version="4"
      >
        <TableHeaderColumn dataSort dataField="Id" isKey hidden>
          Id
        </TableHeaderColumn>
        <TableHeaderColumn dataSort dataField="Game">
          Game
        </TableHeaderColumn>
        <TableHeaderColumn dataSort dataField="Odds" dataAlign="right">
          Odds
        </TableHeaderColumn>
        <TableHeaderColumn dataSort dataField="OddsMinMax" dataAlign="left">
          Min/Max
        </TableHeaderColumn>
      </BootstrapTable>
    </>
  );

  const render = (values) => {
    switch (values?.[0].IdSport?.toLowerCase().trim()) {
      case "nba":
      case "nfl":
        return nflNba(values?.[0]);
      case "mlb":
        return mlb(values?.[0]);
      case "nhl":
        return nhl(values?.[0]);
      case "cbb":
        return cbb(values?.[0]);
      case "soc":
        return soc(values?.[0]);
      case "mu":
        return mu(values?.[0]);
      case "tnt":
        return tnt(values);
      default:
        return <></>;
    }
  };

  if (loading) return <Loader />;
  return (
    <div id="main-div" className={`${className} !h-max flex flex-col gap-4`}>
      <div className="flex flex-row gap-4 mb-4">
        <Button
          type="button"
          onClick={onSearch}
          color="primary"
          variant="contained"
        >
          Search
        </Button>
      </div>

      <h2>Agent Position Report</h2>
      {!_.isEmpty(data) ? (
        <Paper className="flex flex-col gap-4">
          {_.map(data, (values, key) => (
            <React.Fragment key={key}>{render(values)}</React.Fragment>
          ))}
        </Paper>
      ) : null}
    </div>
  );
}

export default ReportAgentPosition;
